/**
 * Utilities for parsing and normalizing PI tag names according to ISA standards.
 */

/** BOP prefix pattern: BOP1., BOP2., BOP3., etc. */
const BOP_PREFIX_PATTERN = /^BOP\d+\./i

/**
 * ISA instrument type suffixes that often measure the same thing:
 * - PT/PI/PE (Pressure Transmitter/Indicator/Element)
 * - TT/TI/TE (Temperature)
 * - FT/FI/FE (Flow)
 * - LT/LI/LE (Level)
 * - AT/AI/AE/AIT (Analyzer)
 */
const ISA_TYPE_PATTERN = /^(\d+-)([A-Z])([TIEAC])([-\w]+)$/i

/**
 * Extracts the core tag name by removing BOP prefix and normalizing ISA type suffix.
 * @example "BOP1.01-PT-RWS101A" -> "01-P-RWS101A"
 * @example "01-PI-RWS101A" -> "01-P-RWS101A"
 */
export function getNormalizedCoreName(piTag: string): string {
  let core = piTag.replace(BOP_PREFIX_PATTERN, '')

  const match = core.match(ISA_TYPE_PATTERN)
  if (match) {
    const [, prefix, firstLetter, , suffix] = match
    core = `${prefix}${firstLetter}-${suffix}`
  }

  return core.toUpperCase()
}

/**
 * Checks if a tag has a BOP prefix.
 */
export function hasBopPrefix(piTag: string): boolean {
  return BOP_PREFIX_PATTERN.test(piTag)
}

/**
 * Checks if a tag has a "Transmitter" type (T suffix), which is preferred over Indicators (I) or Elements (E).
 */
export function isTransmitterType(piTag: string): boolean {
  return /^(\d+-)?[A-Z]T[-\d]/i.test(piTag.replace(BOP_PREFIX_PATTERN, ''))
}

interface Datapoint {
  id: string
  pi_tag: string
  name: string
  description: string
}

/**
 * All datastreams linked to a subject: probes the name and every alias (the
 * tag↔unit link is by text), deduplicated via {@link deduplicateDatapoints}.
 */
export function matchSubjectDatapoints<T extends Datapoint>(
  subject: { name: string; aliases: Array<{ value: string }> } | undefined,
  getMatchedDatapoints: (text: string | undefined) => T[]
): T[] {
  if (!subject) return []
  const byId = new Map<string, T>()
  for (const text of [subject.name, ...subject.aliases.map((a) => a.value)]) {
    for (const datapoint of getMatchedDatapoints(text)) {
      if (!byId.has(datapoint.id)) byId.set(datapoint.id, datapoint)
    }
  }
  return deduplicateDatapoints([...byId.values()])
}

/**
 * Deduplicates datapoints, preferring:
 * 1. Clean tags over BOP-prefixed ones
 * 2. Transmitter types (PT, TT) over Indicator/Element types (PI, TI, TE)
 */
export function deduplicateDatapoints<T extends Datapoint>(
  datapoints: T[]
): T[] {
  const byCoreName = new Map<string, T[]>()

  for (const dp of datapoints) {
    const coreName = getNormalizedCoreName(dp.pi_tag)
    const existing = byCoreName.get(coreName) || []
    existing.push(dp)
    byCoreName.set(coreName, existing)
  }

  const result: T[] = []
  for (const group of byCoreName.values()) {
    if (group.length === 1) {
      result.push(group[0])
      continue
    }

    const cleanTransmitter = group.find(
      (dp) => !hasBopPrefix(dp.pi_tag) && isTransmitterType(dp.pi_tag)
    )
    if (cleanTransmitter) {
      result.push(cleanTransmitter)
      continue
    }

    const cleanOther = group.find((dp) => !hasBopPrefix(dp.pi_tag))
    if (cleanOther) {
      result.push(cleanOther)
      continue
    }

    const bopTransmitter = group.find((dp) => isTransmitterType(dp.pi_tag))
    if (bopTransmitter) {
      result.push(bopTransmitter)
      continue
    }

    result.push(group[0])
  }

  return result
}
